
import Vue from 'vue';
import store from '@/store';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Action, namespace, State } from 'vuex-class';
import { Image, ImageMonth } from '@/types/image';
import InfiniteLoading from 'vue-infinite-loading';
import { StateChanger } from 'vue-infinite-loading';
import ImageTile from '@/components/ImageManager/ImageTile.vue';
import { Tag } from '@/types/tag';
import TagDialog from '@/components/ImageManager/TagDialog.vue';
import { debounce } from 'typescript-debounce-decorator';
import ImageUploadDialog from '@/components/ImageManager/ImageUploadDialog.vue';

const ImageStore = namespace('images');

@Component({
  components: { ImageUploadDialog, ImageTile, InfiniteLoading, TagDialog },
  filters: {
    lowRes(url: string) {
      return url.replace('/images/', '/250x250/filters:format(webp)/images/');
    },
  },
})
export default class ImageContainer extends Vue {
  @Prop() images!: Image[];
  @Prop() imageMonths!: ImageMonth[];
  @Prop({ default: false })
  modal!: boolean;
  @Action('tags/getMatchingTags') getMatchingTags!: Function;
  @Action('images/deleteImage') deleteImage!: Function;
  @Action('images/updateTags') updateImageTags!: Function;
  @ImageStore.State('current_page') currentPage!: number;
  @ImageStore.State('last_page') lastPage!: number;
  @State(state => state.tags.matchingTags)
  tags!: Tag[];

  deleteDialog: boolean = false;
  previewDialog: boolean = false;
  taggingDialog: boolean = false;
  selectedImage: Image | { id: string; tags: string[] } = { id: '', tags: [] };

  tagSearchTerm: string = '';
  tagsLoading: boolean = false;

  filters: { monthStarting: string; tags: string[] } = {
    monthStarting: 'all',
    tags: [],
  };

  async infiniteHandler($state: StateChanger) {
    if (this.lastPage > this.currentPage) {
      await store.dispatch('images/getPage', {
        page: this.currentPage + 1,
        filters: this.filters,
      });
      $state.loaded();
    } else {
      $state.complete();
    }
  }

  onConfirmDelete() {
    this.deleteDialog = false;
    this.previewDialog = false;
    if (this.selectedImage) {
      this.deleteImage({ id: this.selectedImage.id });
    }
  }

  onUpdateImageTags(tags: string[]) {
    if (this.selectedImage !== null) {
      this.updateImageTags({
        id: this.selectedImage.id,
        tags,
      });
    }
  }

  onUploadComplete() {
    store.dispatch('images/getPage', { page: 1, filters: this.filters });
  }

  preview(image: Image): void {
    this.selectedImage = image;
    this.previewDialog = true;
  }

  removeTag(item: Tag) {
    const index = this.filters.tags.indexOf(item.name);
    if (index >= 0) this.filters.tags.splice(index, 1);
  }

  insertImage() {
    this.previewDialog = false;
    this.$emit('selected', this.selectedImage);
  }

  @Watch('filters', { deep: true })
  onImageMonthChange(newVal: string) {
    store.dispatch('images/getPage', { page: 1, filters: newVal });
  }

  @Watch('tagSearchTerm')
  @debounce(275, { leading: false })
  async onTagSearchTermChanged(term: string) {
    if (term && term.length > 0) {
      this.tagsLoading = true;
      await this.getMatchingTags({ term });
      this.tagsLoading = false;
    }
  }
}
